import * as Yup from "yup";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const calculateAge = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
};

export const signUpValidationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must not exceed 50 characters")
    .matches(/^[a-zA-Z\s]+$/, "First name should only contain letters")
    .required("Please enter First Name"),
    
  lastName: Yup.string()
    .trim()
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must not exceed 50 characters")
    .matches(/^[a-zA-Z\s]+$/, "Last name should only contain letters")
    .required("Please enter Last Name"),
    
  displayName: Yup.string()
    .trim()
    .min(3, "Display name must be at least 3 characters")
    .max(20, "Display name must not exceed 20 characters")
    .matches(/^[a-zA-Z0-9_]+$/, "Display name can only contain letters, numbers, and underscores")
    .required("Please enter Display Name"),
    
  email: Yup.string()
    .trim()
    .lowercase()
    .email("Please enter a valid Email Address")
    .required("Please enter Email Address"),
    
  phoneNumber: Yup.string()
    .required("Please enter Phone Number")
    .test("is-valid-phone", "Please enter a valid Phone Number", function (value) {
      if (!value) return false;
      try {
        const phoneNumber = parsePhoneNumberFromString(`+${value}`);
        return phoneNumber && phoneNumber.isValid();
      } catch (error) {
        return false;
      }
    }),

  dateOfBirth: Yup.date()
    .required("Please enter your Date of Birth")
    .max(new Date(), "Date of birth cannot be in the future")
    .test("age-validation", "You must be at least 18 years old", function (value) {
      if (!value) return false;
      const age = calculateAge(value);
      return age >= 18;
    }),

  addressLine1: Yup.string()
    .trim()
    .min(5, "Address Line 1 must be at least 5 characters")
    .max(100, "Address Line 1 must not exceed 100 characters")
    .required("Please enter Address Line 1"),

  addressLine2: Yup.string()
    .trim()
    .max(100, "Address Line 2 must not exceed 100 characters"),

  city: Yup.string()
    .trim()
    .min(2, "City/Town must be at least 2 characters")
    .max(50, "City/Town must not exceed 50 characters")
    .matches(/^[a-zA-Z\s\-']+$/, "City/Town should only contain letters, spaces, hyphens, and apostrophes")
    .required("Please enter City/Town"),

  region: Yup.string()
    .trim()
    .min(2, "Region/State/Province must be at least 2 characters")
    .max(50, "Region/State/Province must not exceed 50 characters")
    .required("Please enter Region/State/Province"),

  country: Yup.string()
    .trim()
    .required("Please select your Country"),

  nationality: Yup.string()
    .trim()
    .required("Please select your Nationality"),
    
  password: Yup.string()
    .required("Please enter Password")
    .min(8, "Password must be at least 8 characters")
    .max(30, "Password must not exceed 30 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
      "Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character"
    ),
    
  confirmPassword: Yup.string()
    .required("Please confirm your Password")
    .oneOf([Yup.ref("password")], "Passwords must match"),
    
  agreeToTerms: Yup.boolean()
    .oneOf([true], "You must agree to the Terms & Conditions")
    .required("You must agree to the Terms & Conditions"),
});

export const emailValidationSchema = Yup.object({
    email: Yup.string()
        .trim()
        .lowercase()
        .email("Please enter a valid Email Address")
        .required("Please enter Email Address"),
});

export const resetPasswordValidationSchema = Yup.object({
  newPassword: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("New Password is required"),
  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm New Password is required"),
});